import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Divider,
  MenuItem,
  Chip,
  IconButton,
} from "@mui/material";
import {
  CardGiftcardOutlined as GiftCardIcon,
  ScheduleOutlined as ScheduleIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  MonetizationOnOutlined as PriceIcon,
  ImageOutlined as ImageIcon,
  CloudUploadOutlined as UploadIcon,
  Close as CloseIcon,
  CheckCircleOutline as ValidIcon,
  VolunteerActivismOutlined as DonateIcon,
  StorefrontOutlined as SellIcon,
} from "@mui/icons-material";
import BigNumber from "bignumber.js";
import web3 from "../../../Service/web3";
import { getTokenInstance } from "../../../Service/tokencinstance";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import {
  CommonTextField,
  CommonDateTimePicker,
  CommonButton,
} from "../../../Components/ui";

const CreateGiftCardForm = ({
  giftcardType,
  setGiftCardType,
  expiry,
  setExpiry,
  tokenAddress,
  settokenAddress,
  amount,
  setAmount,
  price,
  setPrice,
  desc,
  setDesc,
  file,
  setfile,
  validated,
  loading,
  currencySymbol = "POL",
  onSubmit,
}) => {
  const fileInputRef = useRef(null);
  const [tokenInfo, setTokenInfo] = useState({ symbol: "", decimals: "", loading: false, error: false });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Generate preview URL when file changes
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  // Fetch token info when token address is valid
  useEffect(() => {
    let isMounted = true;
    if (tokenAddress && web3 && web3.utils && web3.utils.isAddress(tokenAddress.trim())) {
      setTokenInfo((prev) => ({ ...prev, loading: true, error: false }));
      try {
        const instance = getTokenInstance(tokenAddress.trim());
        Promise.all([
          instance.methods.symbol().call().catch(() => "TOKEN"),
          instance.methods.decimals().call().catch(() => 18),
        ])
          .then(([symbol, decimals]) => {
            if (isMounted) {
              setTokenInfo({
                symbol: symbol || "TOKEN",
                decimals: decimals || 18,
                loading: false,
                error: false,
              });
            }
          })
          .catch(() => {
            if (isMounted) {
              setTokenInfo({ symbol: "", decimals: "", loading: false, error: true });
            }
          });
      } catch {
        if (isMounted) {
          setTokenInfo({ symbol: "", decimals: "", loading: false, error: true });
        }
      }
    } else {
      setTokenInfo({ symbol: "", decimals: "", loading: false, error: false });
    }
    return () => {
      isMounted = false;
    };
  }, [tokenAddress]);

  const isDonate = giftcardType === "1";

  // Address validation
  const isAddressInvalid =
    validated &&
    (!tokenAddress ||
      tokenAddress.trim() === "" ||
      !web3.utils.isAddress(tokenAddress.trim()));

  // Amount validation
  const isAmountInvalid =
    validated &&
    (!amount ||
      amount.trim() === "" ||
      Number(amount) <= 0 ||
      BigNumber(amount).isLessThanOrEqualTo(0));

  // Price validation (only when Sell)
  const isPriceInvalid =
    !isDonate &&
    validated &&
    (!price ||
      price.trim() === "" ||
      Number(price) <= 0 ||
      BigNumber(price).isLessThanOrEqualTo(0));

  // Description validation
  const isDescInvalid = validated && (!desc || desc.trim() === "");

  // File validation
  const isFileInvalid = validated && !file;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setfile(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith("image/")) {
        setfile(droppedFile);
      }
    }
  };

  return (
    <Box component="form" noValidate onSubmit={onSubmit} sx={{ width: "100%" }}>
      {/* ========================================================================= */}
      {/* Section 1: Gift Card Mode & Expiration */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(37, 99, 235, 0.1)",
              color: colors.primary.main,
            }}
          >
            <GiftCardIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            Gift Card Mode & Schedule
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CommonTextField
              select
              label="Gift Card Type"
              value={giftcardType}
              onChange={(e) => setGiftCardType(e.target.value)}
            >
              <MenuItem value="0">Sell</MenuItem>
              <MenuItem value="1">Donate</MenuItem>
            </CommonTextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <CommonDateTimePicker
              label="Expiry Date"
              value={expiry}
              onChange={(value) => setExpiry(value)}
              minDate={new Date()}
              maxDate={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)}
              format="dd-MM-yy h:mma"
              disableClock
              calendarIcon={<ScheduleIcon sx={{ fontSize: 18, color: colors.slate[400] }} />}
              clearIcon={null}
            />
          </Grid>
        </Grid>

        {/* Dynamic Mode Information Box */}
        <Box
          sx={{
            mt: 1.5,
            py: 1,
            px: 1.5,
            borderRadius: borderRadiusPx.lg,
            bgcolor: isDonate ? "rgba(16, 185, 129, 0.06)" : colors.slate[50],
            border: `1px solid ${isDonate ? "rgba(16, 185, 129, 0.2)" : colors.dashboard.cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            {isDonate ? (
              <DonateIcon sx={{ fontSize: 16, color: colors.emerald[600] }} />
            ) : (
              <SellIcon sx={{ fontSize: 16, color: colors.primary.main }} />
            )}
          </Box>
          <Typography sx={{ fontSize: "12.5px", color: colors.slate[600], lineHeight: 1.4 }}>
            <strong style={{ color: isDonate ? colors.emerald[700] : colors.slate[800] }}>
              {isDonate ? "Donation Mode:" : "Commercial Mode:"}
            </strong>{" "}
            {isDonate
              ? "The locked tokens will be freely claimable by anyone without any payment."
              : `Users will pay the specified price in ${currencySymbol} to purchase and unlock the card's tokens.`}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

      {/* ========================================================================= */}
      {/* Section 2: Token Configuration & Pricing */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(16, 185, 129, 0.1)",
              color: colors.emerald[600],
            }}
          >
            <WalletIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            Token & Value
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CommonTextField
              label="Token Address (ERC-20)"
              placeholder="0x..."
              value={tokenAddress}
              onChange={(e) => settokenAddress(e.target.value)}
              error={isAddressInvalid}
              helperText={
                isAddressInvalid
                  ? !tokenAddress || tokenAddress.trim() === ""
                    ? "Please enter a valid ERC-20 token address"
                    : "Invalid token contract address"
                  : tokenInfo.symbol
                  ? `Token identified: ${tokenInfo.symbol} (${tokenInfo.decimals} decimals)`
                  : "Enter the ERC-20 contract address of the token to be locked"
              }
              endAdornment={
                tokenInfo.symbol ? (
                  <Chip
                    icon={<ValidIcon style={{ color: colors.emerald[600], fontSize: 14 }} />}
                    label={tokenInfo.symbol}
                    size="small"
                    sx={{
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                      color: colors.emerald[700],
                      fontWeight: 600,
                      fontSize: "12px",
                      borderRadius: borderRadiusPx.full,
                    }}
                  />
                ) : null
              }
            />
          </Grid>

          <Grid item xs={12} sm={isDonate ? 12 : 6}>
            <CommonTextField
              label={`Token Amount (${tokenInfo.symbol || "Tokens"})`}
              placeholder="e.g. 100"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={isAmountInvalid}
              helperText={
                isAmountInvalid
                  ? !amount || amount.trim() === ""
                    ? "Please enter the token amount"
                    : BigNumber(amount).isLessThan(0)
                    ? "Amount cannot be negative"
                    : "Amount must be greater than 0"
                  : "Total tokens that will be deposited and locked in this card"
              }
              startAdornment={<WalletIcon sx={{ fontSize: 18, color: colors.slate[400] }} />}
            />
          </Grid>

          {!isDonate && (
            <Grid item xs={12} sm={6}>
              <CommonTextField
                label={`Selling Price (${currencySymbol})`}
                placeholder="e.g. 1.5"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                error={isPriceInvalid}
                helperText={
                  isPriceInvalid
                    ? !price || price.trim() === ""
                      ? `Please enter selling price in ${currencySymbol}`
                      : BigNumber(price).isLessThan(0)
                      ? "Price cannot be negative"
                      : "Price must be greater than 0"
                    : `Amount buyer must pay in ${currencySymbol} to purchase`
                }
                startAdornment={<PriceIcon sx={{ fontSize: 18, color: colors.slate[400] }} />}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

      {/* ========================================================================= */}
      {/* Section 3: Customization & Branding */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(168, 85, 247, 0.1)",
              color: colors.purple ? colors.purple[600] : "#9333ea",
            }}
          >
            <ImageIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            Card Design & Description
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CommonTextField
              multiline
              rows={3}
              label="Description"
              placeholder="Enter Description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              error={isDescInvalid}
              helperText={
                isDescInvalid
                  ? "Enter Description"
                  : "Message or greeting visible to the recipient or buyer"
              }
            />
          </Grid>

          {/* Card Image Upload Zone */}
          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: colors.slate[700],
                mb: 1,
              }}
            >
              Card Graphic / Image <span style={{ color: colors.error?.main || "#ef4444" }}>*</span>
            </Typography>

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {!file ? (
              <Box
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  p: 3,
                  border: `2px dashed ${
                    isFileInvalid
                      ? colors.error?.main || "#ef4444"
                      : isDragOver
                      ? colors.primary.main
                      : colors.slate[300]
                  }`,
                  borderRadius: borderRadiusPx.xl,
                  bgcolor: isDragOver
                    ? "rgba(37, 99, 235, 0.04)"
                    : colors.slate[50],
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: colors.primary.main,
                    bgcolor: "rgba(37, 99, 235, 0.02)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: "rgba(37, 99, 235, 0.1)",
                    color: colors.primary.main,
                    mb: 1.25,
                  }}
                >
                  <UploadIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: colors.slate[800],
                    mb: 0.5,
                  }}
                >
                  Click to upload or drag and drop card graphic
                </Typography>
                <Typography sx={{ fontSize: "12px", color: colors.slate[500] }}>
                  PNG, JPG, WEBP, or GIF (Recommended ratio 16:9 or 3:2)
                </Typography>
                {isFileInvalid && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: "12px",
                      color: colors.error?.main || "#ef4444",
                      fontWeight: 500,
                    }}
                  >
                    Please select a card image
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: borderRadiusPx.xl,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  bgcolor: colors.slate[50],
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}>
                  {previewUrl && (
                    <Box
                      component="img"
                      src={previewUrl}
                      alt="Card Preview"
                      sx={{
                        width: 56,
                        height: 56,
                        objectFit: "cover",
                        borderRadius: borderRadiusPx.md,
                        border: `1px solid ${colors.slate[200]}`,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 600,
                        fontSize: "13.5px",
                        color: colors.slate[900],
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: colors.slate[500] }}>
                      {(file.size / 1024).toFixed(1)} KB • Image ready for IPFS upload
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                  <CommonButton
                    variant="text"
                    size="small"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    Change
                  </CommonButton>
                  <IconButton
                    size="small"
                    onClick={() => setfile(null)}
                    sx={{
                      color: colors.slate[500],
                      "&:hover": { color: colors.error?.main || "#ef4444" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* ========================================================================= */}
      {/* Section 4: Live Summary Card */}
      {/* ========================================================================= */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: borderRadiusPx.xl,
          bgcolor: colors.slate[50],
          border: `1px solid ${colors.dashboard.cardBorder}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: colors.slate[500],
            mb: 1.5,
          }}
        >
          Gift Card Summary
        </Typography>

        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: "11.5px", color: colors.slate[500] }}>
              Card Type
            </Typography>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: colors.slate[900] }}>
              {isDonate ? "Donation (Free)" : "Commercial (Sale)"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: "11.5px", color: colors.slate[500] }}>
              Token Deposit
            </Typography>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: colors.primary.main }}>
              {amount || "0"} {tokenInfo.symbol || "Tokens"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: "11.5px", color: colors.slate[500] }}>
              Price
            </Typography>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: isDonate ? colors.emerald[600] : colors.slate[900] }}>
              {isDonate ? "0 (Free Claim)" : `${price || "0"} ${currencySymbol}`}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: "11.5px", color: colors.slate[500] }}>
              IPFS Artwork
            </Typography>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: file ? colors.emerald[600] : colors.slate[400] }}>
              {file ? "Attached" : "Not Selected"}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* ========================================================================= */}
      {/* Submit Action */}
      {/* ========================================================================= */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
        <CommonButton
          type="submit"
          variant="contained"
          size="medium"
          loading={loading}
          disabled={loading}
          sx={{
            minWidth: { xs: "100%", sm: "200px" },
            py: 1.25,
            fontWeight: 700,
            fontSize: "14.5px",
            boxShadow: customShadows.buttonPrimary,
          }}
          startIcon={<GiftCardIcon />}
        >
          {loading ? "Deploying..." : "Create Gift Card"}
        </CommonButton>
      </Box>
    </Box>
  );
};

CreateGiftCardForm.propTypes = {
  giftcardType: PropTypes.string.isRequired,
  setGiftCardType: PropTypes.func.isRequired,
  expiry: PropTypes.instanceOf(Date).isRequired,
  setExpiry: PropTypes.func.isRequired,
  tokenAddress: PropTypes.string.isRequired,
  settokenAddress: PropTypes.func.isRequired,
  amount: PropTypes.string.isRequired,
  setAmount: PropTypes.func.isRequired,
  price: PropTypes.string.isRequired,
  setPrice: PropTypes.func.isRequired,
  desc: PropTypes.string.isRequired,
  setDesc: PropTypes.func.isRequired,
  file: PropTypes.object,
  setfile: PropTypes.func.isRequired,
  validated: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  currencySymbol: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};

export default CreateGiftCardForm;
